import React, { useCallback, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { useLazyQuery } from '@apollo/client';
import {
  IFieldFieldsFragment,
  IGetFieldValuesQuery,
  IGetFieldValuesQueryVariables,
  PermissionKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import { GET_FIELD_VALUES } from '../../../../apollo';
import { useAppSelector } from '../../../../../../redux/hooks';
import { activeSchemaIdSelector } from '../../../../../../redux/selectors/builder';
import { CreatableReactSelectMenu } from '../../../../../../components/Dropdown/StyledReactSelect';
import { useDebounce } from '../../../../../../utils/hooks';
import { colors } from '../../../../../../config/styles';
import { BasicSingleValue } from '../../../../../../components/Dropdown/StyledReactSelect/SelectUtils';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import { createRefreshableMenuComponent } from '../../../../../../components/DataRefresh/RefreshableMenuList';
import PfvOption from '../../../../../../components/ParentFieldValue/PfvOption';
import { getParentFieldValueTooltip } from '../../../../../../components/ParentFieldValue/utils';
import {
  IParentFieldValueFilter,
  IReactSelectFieldValueOption,
} from '../../../../../../components/ParentFieldValue/types';
import { defaultEventInput } from '../../../../../../utils/builder';
import { getFieldValueKey } from '../../../../../../utils/analytics/events/filter/utils';

interface IProps {
  field: IFieldFieldsFragment;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  hasError?: boolean;
  parentFieldValueFilter?: IParentFieldValueFilter[];
}

const SignificanceFieldValueMenu = ({
  field,
  value,
  onChange,
  placeholder,
  hasError,
  parentFieldValueFilter,
}: IProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadFields = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId: activeSchemaId,
  });

  const [loadFieldValues, { data, loading }] = useLazyQuery<
    IGetFieldValuesQuery,
    IGetFieldValuesQueryVariables
  >(GET_FIELD_VALUES, {
    variables: {
      fieldKey: field.key,
      parents: parentFieldValueFilter?.map((pfv) => pfv.value) ?? [],
      term: debouncedSearchQuery,
      schemaId: activeSchemaId,
      includeValues: [],
      events: [defaultEventInput],
    },
  });

  const handleLoadOptions = useCallback(() => {
    if (isPermittedToReadFields) {
      loadFieldValues();
    }
  }, [loadFieldValues, isPermittedToReadFields]);

  const handleChange = (
    newOption: SingleValue<IReactSelectFieldValueOption>,
  ) => {
    onChange(newOption?.value ?? null);
  };

  const tooltip = useMemo(
    () => getParentFieldValueTooltip(parentFieldValueFilter),
    [parentFieldValueFilter],
  );

  const selectedOption: IReactSelectFieldValueOption | null = useMemo(() => {
    if (value) {
      return {
        label: value,
        value,
      };
    }

    return null;
  }, [value]);

  const options: IReactSelectFieldValueOption[] = useMemo(() => {
    return (
      data?.fieldValues?.map((val) => ({
        label: val.name,
        value: getFieldValueKey(val),
        tooltip,
      })) ?? []
    );
  }, [data?.fieldValues, tooltip]);

  const RefreshableMenu = useMemo(
    () =>
      createRefreshableMenuComponent<
        IReactSelectFieldValueOption,
        false,
        never
      >({
        schemaId: activeSchemaId,
        field,
        onRefreshEnd: handleLoadOptions,
      }),
    [activeSchemaId, field, handleLoadOptions],
  );

  return (
    <CreatableReactSelectMenu
      options={options}
      onMenuOpen={handleLoadOptions}
      onChange={handleChange}
      value={selectedOption}
      isMulti={false}
      isSearchable={true}
      isLoading={loading}
      inputValue={searchQuery}
      onInputChange={setSearchQuery}
      placeholder={placeholder}
      components={{
        SingleValue: BasicSingleValue,
        Option: PfvOption,
        Menu: RefreshableMenu, // we are rendering our custom menu
      }}
      styles={{
        container: (base) => ({
          ...base,
          flex: '0 0 auto',
        }),
        control: (base) => {
          if (hasError) {
            return {
              ...base,
              borderColor: colors.mainError,
            };
          }

          return base;
        },
      }}
      onCreateOption={onChange}
    />
  );
};

export default SignificanceFieldValueMenu;
